import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-context';
import { handleCheckout } from '@/lib/checkout';
import { useState, useEffect } from 'react';
import { track, EVENTS } from '@/lib/mixpanel';
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { TestimonialSection } from '@/components/common/TestimonialSection';
import { StepProgress } from '@/components/common/StepProgress';
import { getLanguagePath } from '@/lib/utils';
import { MetaHead } from '@/components/common/MetaHead';
import { IconArrowLeft } from '@tabler/icons-react';

// TODO: check if this account already has Pro access (with RC)
// if the user has aleardy subscribed, don't allow the button

// TODO: show loading when loading is true

export default function ConfirmAccount() {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const planId = localStorage.getItem('pendingPurchasePlanId');

  useEffect(() => {
    track(EVENTS.VIEW_CONFIRM_ACCOUNT_PAGE, {
      planId,
      provider: user?.app_metadata?.provider
    })

    if (!authLoading && (!user || !planId)) {
      // Only redirect if auth is initialized and we're missing user or planId
      navigate(getLanguagePath(''));
    }
  }, [user, planId, navigate, authLoading]);

  const handleChangeAccount = async () => {
    track(EVENTS.CHANGE_ACCOUNT, {
      provider: user?.app_metadata?.provider,
      email: user?.email
    });
    await signOut();
    navigate(getLanguagePath('sign-in'));
  };

  const handleConfirmPurchase = () => {
    track(EVENTS.INITIATE_CHECKOUT, {
      planId,
      provider: user?.app_metadata?.provider,
      email: user?.email,
    });
    handleCheckout({
      planId: planId!,
      userId: user!.id,
      navigate,
      setIsLoading,
      skipConfirmation: true,
    });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-12">
      <MetaHead
        title={t('meta.signIn.title')}
        description={t('meta.signIn.description')}
      />
      {/* Left Section - Desktop */}
      <div className="hidden lg:flex flex-col lg:col-span-5 bg-slate-950 px-12">
        <div className="p-6 lg:p-10">
          <Link to={getLanguagePath('')}>
            <img
              src="/brands/clarymind-logo_logotype-app-transparent-dark-outline.svg"
              alt="Clarymind"
              className="h-12 w-auto"
            />
          </Link>
        </div>
        <div className="p-6 lg:p-10 mt-auto">
          <TestimonialSection />
        </div>
      </div>

      {/* Right Section - Desktop */}
      <div className="lg:col-span-7 flex flex-col items-center justify-center p-6 lg:p-8">
        {/* <div className="w-full max-w-md">
          <StepProgress currentStep={1} />
        </div> */}

        {/* Back */}
        <div className="w-full max-w-md mx-auto text-center lg:px-4 pt-4 lg:pt-8 pb-2">
          <button
            onClick={() => navigate(getLanguagePath('/'))}
            className="px-4 py-2 text-slate-400 rounded-xl text-sm hover:text-slate-500 flex justify-start items-center gap-1 group transition-all duration-300"
          >
            <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
            {t('checkout.back')}
          </button>
        </div>

        <div className="flex-grow"></div>

        <div className="w-full max-w-xs lg:max-w-md space-y-4">

          {/* Mobile Logo */}
          <div className="lg:hidden top-0 left-0 right-0 p-6 flex justify-center">
            <Link to={getLanguagePath('')}>
              <img
                src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
                alt="Clarymind"
                className="h-12 w-auto"
              />
            </Link>
          </div>

          <h1 className="text-base lg:text-xl font-semibold text-center">
            {t('auth.currentlySignedInAs')}
          </h1>

          <div className="space-y-6">
            <Card className="flex-grow pl-6 pr-3 py-3 bg-slate-50">
              <div className="flex items-center gap-3">
                <img
                  src={user?.app_metadata?.provider === 'google' ? '/brands/google.svg' : '/brands/apple.svg'}
                  alt={`${user?.app_metadata?.provider} logo`}
                  className="w-6 h-6"
                />

                <span className="flex-grow truncate">{user?.email}</span>

                <Button
                  variant="outline"
                  onClick={handleChangeAccount}
                  disabled={isLoading}
                  size="default"
                >
                  {t('common.change')}
                </Button>
              </div>
            </Card>

            {/* <div className="h-px w-full bg-slate-200" /> */}

            <Button
              onClick={handleConfirmPurchase}
              disabled={isLoading}
              className="w-full py-6"
            >
              {isLoading ? t('common.loading') : t('auth.continueToPurchase')}
            </Button>

            <div className="space-y-6 pt-12 pb-4">

              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-slate-200"></div>
                <h2 className="text-sm text-slate-600 px-3 text-center">
                  {t('auth.signInWhy')}
                </h2>
                <div className="h-px flex-1 bg-slate-200"></div>
              </div>

              <p className="max-w-md mx-auto text-sm text-slate-600 text-left px-3">
                {t('auth.signInHint')}
              </p>
            </div>
          </div>

          {/* Mobile Testimonial */}
          {/* <div className="lg:hidden bottom-0 left-0 right-0 p-6">
            <div className="flex flex-col justify-center items-center">
              <div className="h-px w-full bg-slate-200 my-6 max-w-xs" />
              <TestimonialSection />
            </div>
          </div> */}
        </div>

        <div className="flex-grow"></div>
      </div>

    </div>
  );
}